// LogsViewer.jsx
import React, { useEffect, useState, useContext } from "react";
import { observer } from "mobx-react-lite";

import { Context } from "../../../index";
import { processLogMessage } from "./logProcessors";

import "./styles.css";
import { logApiError } from "../../../utils/logApiError";

const DBLogsViewer = () => {
  const { store, adminStore } = useContext(Context);

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]); // Пока не используется, но можно оставить на будущее
  const [chargeIdFilter, setChargeIdFilter] = useState("");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState("{address:[1,2,3], value:[,,,]}");
  const [error, setError] = useState(null);

  // Состояние для отслеживания раскрытых сообщений
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  // Состояние для отслеживания режима отображения сообщений
  const [readableMode, setReadableMode] = useState({});

  useEffect(() => {
    fetchLogs(limit, chargeIdFilter); // Передаем оба параметра
  }, []);

  useEffect(() => {
    // Этот эффект теперь не нужен, так как фильтрация будет на бэкенде
    // const filtered = logs.filter(
    //   (log) =>
    //     !chargeIdFilter ||
    //     (log.chargeId && log.chargeId.includes(chargeIdFilter)),
    // );
    // setFilteredLogs(filtered);
    setFilteredLogs(logs); // Просто присваиваем, т.к. фильтрация на бэке
  }, [logs]); // Зависимость только от logs

  const fetchLogs = async (limit, chargeId) => {
    try {
      setLoading(true);
      // Передаем chargeId в запрос, даже если он пустой
      const data = await store.getLogs(limit, chargeId);
      setLogs(data);
      setError(null);
    } catch (err) {
      setError("Не удалось загрузить логи");
      logApiError("Ошибка при загрузке логов БД:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChargeIdChange = (e) => {
    const newChargeId = e.target.value;
    setChargeIdFilter(newChargeId);
    // Вызываем обновление логов при изменении фильтра
    fetchLogs(limit, newChargeId);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    // Вызываем обновление логов при изменении лимита
    fetchLogs(newLimit, chargeIdFilter);
  };

  // Функция для переключения режима отображения сообщения
  const toggleReadableMode = (logIndex) => {
    setReadableMode((prev) => ({
      ...prev,
      [logIndex]: !prev[logIndex],
    }));
  };

  if (loading) {
    return <div className="logs-container">Загрузка логов...</div>;
  }

  if (error) {
    return <div className="logs-container error">{error}</div>;
  }

  const handleSubmit = async (message, charge_id) => {
    try {
      await adminStore.sendMesage(message, charge_id);
    } catch (error) {
      logApiError("Ошибка:", error);
    }
  };

  // Функция для переключения состояния раскрытия сообщения
  const toggleExpand = (logIndex) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(logIndex)) {
      newExpanded.delete(logIndex);
    } else {
      newExpanded.add(logIndex);
    }
    setExpandedMessages(newExpanded);
  };

  return (
    <div className="logs-container">
      {/* Фильтры */}
      <div>
        {chargeIdFilter ? (
          <div className="filter-group" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: "5px", width: "100%" }}>
              <button
                className="send-button"
                style={{ flex: "0 0 auto", width: "auto" }}
                onClick={() => handleSubmit(inputText, chargeIdFilter)}
              >
                Отправить
              </button>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="filter-input"
                style={{
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
                placeholder="Введите сообщение для отправки"
              />
            </div>
          </div>
        ) : (
          "<чтобы отправить сообщение выберите модуль>"
        )}
      </div>
      <div className="logs-filters">
        <div className="filter-group">
          <select
            value={chargeIdFilter}
            onChange={handleChargeIdChange}
            className="filter-select"
          >
            <option value="">Все модули</option>
            {adminStore.modules.map((module) => (
              <option key={module.id} value={module.charge_id}>
                {module.charge_id}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <input
            type="text"
            value={chargeIdFilter}
            onChange={handleChargeIdChange} /* Изменили обработчик */
            placeholder="Фильтр по chargeId"
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={limit}
            onChange={handleLimitChange}
            className="filter-select"
          >
            <option value={10}>10</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={300}>300</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
        <div className="filter-group">
          <button
            onClick={() => fetchLogs(limit, chargeIdFilter)}
            className="refresh-button"
          >
            Обновить
          </button>
        </div>
      </div>

      {/* Таблица */}
      <table className="logs-table">
        <colgroup>
          <col style={{ width: "155px" }} /> {/* Дата */}
          <col style={{ width: "130px" }} /> {/* Направление */}
          <col style={{ width: "auto" }} /> {/* Сообщение (остаток) */}
          <col style={{ width: "150px" }} /> {/* Устройство */}
        </colgroup>
        <thead>
          <tr>
            <th>Дата</th>
            <th>Направление</th>
            <th>Сообщение</th>
            <th>Устройство</th>
          </tr>
        </thead>
        <tbody>
          {filteredLogs.length > 0 ? (
            filteredLogs.map((log, index) => {
              const isReadable = readableMode[index];
              const messageContent = isReadable
                ? processLogMessage(log.message)
                : JSON.stringify(log.message);

              return (
                <tr key={index}>
                  <td>
                    <small>{new Date(log.createdAt).toLocaleString()}</small>
                  </td>
                  <td>
                    <small>{log.type}</small>
                  </td>
                  {/* Ячейка с сообщением */}
                  <td className="log-message">
                    <div className="message-wrapper">
                      {messageContent.length > 100 ? (
                        <div
                          className="toggle-readable-btn"
                          onClick={() => toggleReadableMode(index)}
                        >
                          {isReadable ? "}{" : "[]"}
                        </div>
                      ) : null}

                      <pre className="message-text">{messageContent}</pre>
                    </div>
                  </td>
                  <td>{log.chargeId}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="4" className="no-logs">
                Нет логов, соответствующих фильтру
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default observer(DBLogsViewer);
